<template>
  <div class="modal-card" style="width: auto">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <header class="modal-card-head">
      <p class="modal-card-title has-text-centered">
        {{
          currentCoupon._id ? $tc("coupon.editCoupon") : $tc("coupon.newCoupon")
        }}
      </p>
    </header>

    <section class="modal-card-body">
      <div class="columns">
        <div class="column is-half is-offset-one-quarter">
          <b-field
            class="mb-5"
            :label="$t('coupon.couponCode')"
            label-position="on-border"
            :type="couponCodeFormType"
            :message="
              couponCodeFormType === 'is-danger'
                ? $t('common.requiredField')
                : ''
            "
          >
            <b-input
              v-model.trim="coupon.name"
              @input="uppercase"
              @blur="couponCodeHasBeenTouched = true"
            ></b-input>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('coupon.couponType')"
            label-position="on-border"
            :type="couponCategoryFormType"
            :message="
              couponCategoryFormType === 'is-danger'
                ? $t('common.requiredField')
                : ''
            "
          >
            <b-select
              v-model="coupon.type"
              expanded
              :placeholder="$t('coupon.selectCouponType')"
              @blur="couponTypeHasBeenTouched = true"
            >
              <option value="" disabled>
                {{ $t("coupon.selectCouponType") }}
              </option>

              <option value="absolute">
                {{ $t("coupon.couponTypeAbsolute") }}
              </option>

              <option value="percentual">
                {{ $t("coupon.couponTypePercentual") }}
              </option>
            </b-select>
          </b-field>

          <b-field
            class="mb-5"
            v-show="coupon.type === 'absolute' || coupon.type === 'percentual'"
            :label="$t('coupon.couponValue')"
            label-position="on-border"
            :type="valueFormType"
            :message="
              valueFormType === 'is-danger'
                ? $t('coupon.couponRequiredMinValue')
                : ''
            "
          >
            <money
              v-model="coupon.value"
              v-bind="
                coupon.type === 'percentual'
                  ? percentualValueSyntax
                  : coupon.type === 'absolute'
                  ? absoluteValueSyntax
                  : {}
              "
              @blur.native="valueFormHasBeenTouched = true"
              class="input is-expanded"
            ></money>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('coupon.couponQtyPerUser')"
            label-position="on-border"
          >
            <b-input
              type="number"
              min="1"
              v-model="coupon.qtyPerUser"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            >
            </b-input>
          </b-field>

          <b-field class="mb-5">
            <b-switch
              v-model="limitTotalUse"
              type="is-primary"
              @input="onToggleLimitTotalUse"
            >
              {{ $t("coupon.limitTotalUseLabel") }}
            </b-switch>
          </b-field>

          <b-field
            v-show="limitTotalUse"
            class="mb-5"
            :label="$t('coupon.couponGlobalUseLimit')"
            label-position="on-border"
            :type="globalUseFormType"
            :message="
              globalUseFormType === 'is-danger'
                ? $t('coupon.couponGlobalUseRequired')
                : ''
            "
          >
            <b-input
              type="number"
              min="1"
              v-model="coupon.qtyGlobalUse"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            >
            </b-input>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('coupon.couponMinCartValue')"
            label-position="on-border"
            :type="minCartValueFormType"
            :message="
              minCartValueFormType === 'is-danger'
                ? $t('coupon.couponRequiredMinValue')
                : ''
            "
          >
            <money
              v-bind="absoluteValueSyntax"
              v-model="coupon.minimumCartValue"
              class="input is-expanded"
              @blur.native="minCartValueHasBeenTouched = true"
            >
            </money>
          </b-field>

          <b-field
            class="mb-5"
            :label="$t('coupon.couponBeginDateLabel')"
            label-position="on-border"
          >
            <b-input
              :placeholder="$t('coupon.couponSelectBeginDate')"
              icon="calendar-day"
              custom-class="is-clickable"
              @click.native="openBeginDateModal"
              @focus="openBeginDateModal"
              id="beginDateId"
              :value="getBeginDateFormattedValue"
            ></b-input>
          </b-field>

          <b-modal
            :active.sync="isBeginDateModalActive"
            can-cancel
            :on-cancel="saveBeginDateAfterModalClose"
          >
            <b-datetimepicker
              v-model="coupon.beginDate"
              inline
              class="has-text-centered"
              locale="pt-BR"
            >
              <template #right>
                <b-button
                  :label="$t('common.ok')"
                  type="is-primary"
                  @click="
                    saveBeginDateAfterModalClose;
                    isBeginDateModalActive = false;
                  "
                />
              </template>
            </b-datetimepicker>
          </b-modal>

          <b-field
            class="mb-5"
            :label="$t('coupon.couponEndDateLabel')"
            label-position="on-border"
          >
            <b-input
              :placeholder="$t('coupon.couponSelectEndDate')"
              icon="calendar-day"
              custom-class="is-clickable"
              @click.native="openEndDateModal"
              @focus="openEndDateModal"
              id="endDateId"
              :value="getEndDateFormattedValue"
            ></b-input>
          </b-field>

          <b-modal
            :active.sync="isEndDateModalActive"
            can-cancel
            :on-cancel="saveEndDateAfterModalClose"
          >
            <b-datetimepicker
              v-model="coupon.endDate"
              inline
              class="has-text-centered"
              locale="pt-BR"
            >
              <template #right>
                <b-button
                  :label="$t('common.ok')"
                  type="is-primary"
                  @click="
                    saveEndDateAfterModalClose;
                    isEndDateModalActive = false;
                  "
                />
              </template>
            </b-datetimepicker>
          </b-modal>

          <b-field
            :label="$t('common.status')"
            label-position="on-border"
            class="mb-5"
          >
            <div class="block" style="position: relative; top: 10px;">
              <b-radio
                v-model="coupon.isActive"
                name="coupon.isActive"
                :native-value="true"
              >
                {{ $t("common.Active") }}
              </b-radio>

              <b-radio
                v-model="coupon.isActive"
                name="coupon.isActive"
                :native-value="false"
              >
                {{ $t("common.Inactive") }}
              </b-radio>
            </div>
          </b-field>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot" style="justify-content: space-around;">
      <div class="columns">
        <div class="column">
          <button class="button" type="button" @click="$parent.close()">
            {{ $t("common.cancel") }}
          </button>

          <button
            class="button"
            :style="{
              backgroundColor: $primaryColor,
              color: $primaryColorContrast
            }"
            type="button"
            @click="createOrEdit"
            :disabled="!isAbleToRegister"
          >
            {{
              currentCoupon._id
                ? $t("coupon.editCoupon")
                : $t("coupon.createCoupon")
            }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { common } from "../../../mixins/common";
import { mask } from "../../../mixins/mask";
import { responsive } from "../../../mixins/responsive";
import { Money } from "v-money";

export default {
  mixins: [common, mask, responsive],

  components: {
    Money
  },

  props: ["currentCoupon"],

  data() {
    return {
      isLoading: false,
      couponCodeHasBeenTouched: false,
      couponTypeHasBeenTouched: false,
      minCartValueHasBeenTouched: false,
      valueFormHasBeenTouched: false,
      beginDateFormHasBeenTouched: false,
      endDateFormHasBeenTouched: false,
      isBeginDateModalActive: false,
      isEndDateModalActive: false,
      limitTotalUse: false,
      coupon: {
        name: "",
        type: "",
        value: 0,
        isActive: true,
        qtyPerUser: 1,
        qtyGlobalUse: 0,
        minimumCartValue: 0,
        beginDate: null,
        endDate: null
      },
      absoluteValueSyntax: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        precision: 2
      },
      percentualValueSyntax: {
        suffix: " %",
        precision: 0
      }
    };
  },

  methods: {
    uppercase() {
      this.coupon.name = this.coupon.name.toUpperCase();
    },

    onToggleLimitTotalUse(value) {
      if (value) {
        if (!this.coupon.qtyGlobalUse || Number(this.coupon.qtyGlobalUse) < 1) {
          this.coupon.qtyGlobalUse = 1;
        }
      } else {
        this.coupon.qtyGlobalUse = 0;
      }
    },

    createOrEdit() {
      return this.currentCoupon._id ? this.updateCoupon() : this.createCoupon();
    },

    createCoupon() {
      if (this.coupon.beginDate > this.coupon.endDate) {
        this.alertError({
          message: "COUPON_BEGIN_DATE_GREATER_THAN_END_DATE"
        });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("createCoupon", {
          storeId: this.storeBeingManaged._id,
          coupon: {
            ...this.coupon,
            value:
              this.coupon.type === "absolute"
                ? this.formatMoneyToCents(this.coupon.value)
                : this.coupon.value,
            minimumCartValue: this.formatMoneyToCents(
              this.coupon.minimumCartValue
            )
          }
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$emit("increment-key");
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("coupon.couponHasBeenCreated"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.$parent.close();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateCoupon() {
      if (this.coupon.beginDate > this.coupon.endDate) {
        this.alertError({
          message: "COUPON_BEGIN_DATE_GREATER_THAN_END_DATE"
        });
        return;
      }
      this.isLoading = true;
      this.$store
        .dispatch("updateCoupon", {
          storeId: this.storeBeingManaged._id,
          coupon: {
            ...this.coupon,
            value:
              this.coupon.type === "absolute"
                ? this.formatMoneyToCents(this.coupon.value)
                : this.coupon.value,
            minimumCartValue: this.formatMoneyToCents(
              this.coupon.minimumCartValue
            )
          }
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.$emit("increment-key");
            this.$buefy.toast.open({
              duration: 3000,
              message: this.$t("coupon.couponHasBeenUpdated"),
              position: "is-bottom",
              type: "is-primary"
            });
            this.$parent.close();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    openBeginDateModal() {
      this.isBeginDateModalActive = true;
    },

    openEndDateModal() {
      this.isEndDateModalActive = true;
    },

    saveBeginDateAfterModalClose() {
      if (this.coupon.beginDate !== null) {
        let beginDateEl = document.getElementById("beginDateId");
        beginDateEl.setAttribute("value", this.coupon.beginDate);
      }
    },

    saveEndDateAfterModalClose() {
      if (this.coupon.endDate !== null) {
        let endDateEl = document.getElementById("endDateId");
        endDateEl.setAttribute("value", this.coupon.endDate);
      }
    }
  },

  computed: {
    ...mapGetters([
      "storeBeingManaged",
      "categories",
      "preferences",
      "payments"
    ]),

    getBeginDateFormattedValue() {
      return this.coupon.beginDate !== null
        ? this.moment
            .utc(this.coupon.beginDate)
            .add(this.preferences.timezone, "h")
            .format("DD/MM/YY, HH:mm")
        : "";
    },

    getEndDateFormattedValue() {
      return this.coupon.endDate !== null
        ? this.moment
            .utc(this.coupon.endDate)
            .add(this.preferences.timezone, "h")
            .format("DD/MM/YY, HH:mm")
        : "";
    },

    couponCodeFormType() {
      return this.couponCodeHasBeenTouched && this.coupon.name === ""
        ? "is-danger"
        : this.couponCodeHasBeenTouched
        ? "is-success"
        : "";
    },

    couponCategoryFormType() {
      return this.couponTypeHasBeenTouched && this.coupon.type === ""
        ? "is-danger"
        : "";
    },

    valueFormType() {
      return this.valueFormHasBeenTouched && this.coupon.value === 0
        ? "is-danger"
        : "";
    },

    minCartValueFormType() {
      return this.minCartValueHasBeenTouched &&
        this.coupon.minimumCartValue === 0
        ? "is-danger"
        : "";
    },

    globalUseFormType() {
      return this.limitTotalUse && Number(this.coupon.qtyGlobalUse) < 1
        ? "is-danger"
        : "";
    },

    beginDateFormType() {
      return this.beginDateFormHasBeenTouched && this.coupon.beginDate === null
        ? "is-danger"
        : "";
    },

    endDateFormType() {
      return this.endDateFormHasBeenTouched && this.coupon.endDate === null
        ? "is-danger"
        : "";
    },

    isAbleToRegister() {
      return (
        this.coupon.name !== "" &&
        this.coupon.type !== "" &&
        this.coupon.value > 0 &&
        this.coupon.qtyPerUser >= 1 &&
        (!this.limitTotalUse || Number(this.coupon.qtyGlobalUse) >= 1) &&
        this.coupon.minimumCartValue > 0 &&
        this.coupon.beginDate &&
        this.coupon.endDate
      );
    }
  },

  mounted() {
    this.absoluteValueSyntax.prefix = `${this.payments.currencySymbol} `;

    if (this.currentCoupon) {
      if (this.currentCoupon.type === "absolute") {
        this.coupon = {
          ...this.currentCoupon,
          value: this.currentCoupon.value / 100,
          minimumCartValue: this.currentCoupon.minimumCartValue / 100
        };
      } else if (this.currentCoupon.type === "percentual") {
        this.coupon = {
          ...this.currentCoupon,
          minimumCartValue: this.currentCoupon.minimumCartValue / 100
        };
      }

      if (!this.coupon.qtyGlobalUse) this.coupon.qtyGlobalUse = 0;
      this.limitTotalUse = Number(this.coupon.qtyGlobalUse) > 0;
    }
  },

  watch: {
    "coupon.value"(v) {
      if (this.coupon.type === "percentual") {
        if (v > 100) {
          this.coupon.value = 100;
        } else if (v < 0) {
          this.coupon.value = 0;
        }
      }
    }
  }
};
</script>

<style scoped>
.product-name {
  position: relative;
  top: 6px;
}
</style>
